import { CircleInfoOutlinedIcon } from '@village/icons';
import { Text } from '@village/ui';
import { Image } from 'components/image';
import styled, { css } from 'styled-components';
import { device } from 'utils/device';

const Container = styled.div`
    @media ${device.maxLaptop} {
        order: -1;
    }
`;

const Wrapper = styled.div<{ $isConfirmationPageLayout?: boolean }>`
    background: ${({ theme }) => theme.vui.colors.background.gray};
    color: ${({ theme }) => theme.vui.colors.text.grayDefault};
    border-radius: 20px;
    padding: 2.5rem 2rem;

    ${({ $isConfirmationPageLayout }) =>
        $isConfirmationPageLayout
            ? css`
                  display: flex;
                  flex-direction: row;
                  margin-bottom: 2.5rem;

                  @media ${device.maxTablet} {
                      flex-direction: column;
                  }
              `
            : css`
                  position: relative;

                  @media ${device.maxTablet} {
                      border-radius: 0;
                      padding: 1.25rem 1rem;
                  }
              `}
`;

const Sections = styled.div<{ $isPrn: boolean; $isConfirmationPageLayout: boolean }>`
    margin-top: ${({ $isPrn }) => ($isPrn ? '1.5rem' : '2.5rem')};

    ${({ $isConfirmationPageLayout }) =>
        $isConfirmationPageLayout
            ? css`
                  margin-top: 0;

                  svg {
                      width: 1.5rem;
                      height: 1.5rem;
                  }
              `
            : css`
                  margin-top: 2.5rem;

                  @media ${device.maxTablet} {
                      margin-top: 1.25rem;
                  }
              `}
`;

const ProviderSection = styled.div<{ $isConfirmationPageLayout: boolean }>`
    display: flex;
    align-items: center;
    flex-direction: column;

    ${({ $isConfirmationPageLayout }) =>
        $isConfirmationPageLayout
            ? css`
                  justify-content: center;
                  flex: 50%;

                  @media ${device.maxTablet} {
                      margin-bottom: 2.5rem;
                  }
              `
            : css`
                  @media ${device.maxTablet} {
                      flex-direction: row;
                      align-items: flex-start;
                  }
              `}
`;

const ProviderInfo = styled.div<{ $isConfirmationPageLayout: boolean }>`
    text-align: center;

    @media ${device.maxTablet} {
        text-align: left;
    }

    ${({ $isConfirmationPageLayout }) =>
        $isConfirmationPageLayout &&
        css`
            @media ${device.maxTablet} {
                text-align: center;
            }
        `}
`;

const ProviderImage = styled(Image)<{ $isConfirmationPageLayout: boolean }>`
    border-radius: 50%;
    border: 1px solid #ddd;
    object-fit: cover;
    height: 7.5rem;
    width: 7.5rem;
    margin-bottom: 1rem;

    @media ${device.maxTablet} {
        height: 3rem;
        width: 3rem;
        margin-right: 0.75rem;
        margin-bottom: 0;
    }

    ${({ $isConfirmationPageLayout }) =>
        $isConfirmationPageLayout &&
        css`
            @media ${device.maxTablet} {
                height: 7.5rem;
                width: 7.5rem;
                margin-bottom: 1rem;
            }
        `}
`;

const ProviderName = styled(Text)`
    color: ${({ theme }) => theme.vui.colors.text.grayDefault};
    line-height: 2.25rem;
`;

const ProviderSpeciality = styled(Text)<{ $isMobile: boolean; $isConfirmationPageLayout: boolean }>`
    color: ${({ theme }) => theme.vui.colors.text.grayMuted};

    ${({ $isConfirmationPageLayout }) =>
        $isConfirmationPageLayout &&
        css`
            line-height: 1.375rem;
        `}
`;

const Section = styled.div<{ $isConfirmationPageLayout: boolean }>`
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 1rem;

    @media ${device.maxTablet} {
        gap: 0.75rem;
    }

    ${({ $isConfirmationPageLayout }) =>
        $isConfirmationPageLayout &&
        css`
            @media ${device.maxTablet} {
                gap: 1.5rem;
            }
        `}

    &:not(:last-of-type) {
        margin-bottom: 1.5rem;

        @media ${device.maxTablet} {
            margin-bottom: 0.75rem;
        }

        ${({ $isConfirmationPageLayout }) =>
            $isConfirmationPageLayout &&
            css`
                @media ${device.maxTablet} {
                    margin-bottom: 1.5rem;
                }
            `}
    }
`;

const InfoTitle = styled(Text)<{ $isConfirmationPageLayout: boolean }>`
    color: ${({ theme }) => theme.vui.colors.text.grayDefault};

    @media ${device.maxTablet} {
        display: none;
    }

    ${({ $isConfirmationPageLayout }) =>
        $isConfirmationPageLayout &&
        css`
            margin-bottom: 0.25rem;

            @media ${device.maxTablet} {
                display: block;
            }
        `}
`;

const InfoValue = styled(Text)`
    color: ${({ theme }) => theme.vui.colors.text.grayDefault};
`;

const ExtraInfoValue = styled(Text)`
    margin-top: 0.25rem;
    color: ${({ theme }) => theme.vui.colors.text.graySubtle};
`;

const InlineInfo = styled.div`
    display: grid;
`;

const SectionTitle = styled.p`
    display: block;
    font-size: 1rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
`;

const VisitTypeIconContainer = styled.div`
    margin-right: 0.5rem;
`;

const CloseIconContainer = styled.div`
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background-color: rgba(10, 10, 10, 0.2);
    cursor: pointer;
    width: 1.25rem;
    height: 1.25rem;
    color: #fff;
`;

const AppointmentNotAvailable = styled(Container)`
    display: grid;
    row-gap: 1.5rem;
    justify-content: center;
    text-align: center;
`;

const PrnSection = styled(Container)`
    display: flex;
    background-color: ${({ theme }) => theme.vui.component.tag.default.backgroundColor};
    border-radius: 0.5rem;
    gap: 0.5rem;
    padding: 1rem;
    margin-top: 2rem;
    text-align: left;

    @media ${device.maxTablet} {
        background-color: transparent;
        gap: 0;
        padding: 0;
        margin-top: 0.25rem;
    }
`;

const PrnIcon = styled(CircleInfoOutlinedIcon)`
    color: ${({ theme }) => theme.vui.colors.action.primary.default};
`;

export {
    AppointmentNotAvailable,
    Container,
    Wrapper,
    InfoTitle,
    Section,
    ProviderImage,
    InfoValue,
    SectionTitle,
    InlineInfo,
    VisitTypeIconContainer,
    CloseIconContainer,
    ProviderSection,
    ProviderName,
    ProviderSpeciality,
    Sections,
    ExtraInfoValue,
    ProviderInfo,
    PrnIcon,
    PrnSection,
};
